import { type FC, useMemo } from 'react';

import { Logger } from '../../../../logger/Logger';
import { CoreSessionStorage } from '../../../../storage/session/CoreSessionStorage';
import { useCoreDispatch } from '../../../../store/CoreStore';
import { useMasterVolumeControl } from '../../../../store/slices/settings/hooks/useVolumeControl';
import { useSettingsSlice } from '../../../../store/slices/settings/selectors';
import { settingsActions } from '../../../../store/slices/settings/slice';
import { useIsLargerThanWide } from '../../../../styles/hooks/media';
import { TooltipPositions } from '../../../tooltip/Tooltip';

import { VolumeSliderControlPlain } from './VolumeSliderControlPlain';

export const VolumeSliderControlConnected: FC = () => {
  const dispatch = useCoreDispatch();
  const masterVolume = useSettingsSlice.getMasterChannel().value;
  const previousMasterVolume = useSettingsSlice.getMasterChannel().previousValue;
  const control = useMasterVolumeControl();
  const { value, change, temporaryChange } = control;
  const wasVolumeRestored = useSettingsSlice.getWasVolumeRestored();
  const wasSomeChannelEnabled = useSettingsSlice.getWasAnySoundChannelEnabled();

  const isLargerThanWide = useIsLargerThanWide();

  const handleToggle = (): void => {
    const volume = masterVolume ? 0 : previousMasterVolume;
    // For setting master volume and syncing other channels(game/studio) volume to server
    dispatch(settingsActions.setMasterVolume(volume));
    Logger.logEvent('menuToggleVolume', {
      additional: volume === 0 ? 'false' : 'true',
    });
  };

  const shouldRestoreVolume: boolean = useMemo(() => {
    const lastMasterVolume = CoreSessionStorage.getLastMasterVolume();
    const wasMasterEnabled = lastMasterVolume === null ? true : !!lastMasterVolume;
    return wasMasterEnabled && wasSomeChannelEnabled && !wasVolumeRestored;
  }, [wasSomeChannelEnabled, wasVolumeRestored]);

  return (
    <VolumeSliderControlPlain
      value={value}
      onDrag={temporaryChange}
      onChange={change}
      onToggle={handleToggle}
      tooltipOptions={{
        text: 'CLICK_TO_UNMUTE',
        position: TooltipPositions.Bottom,
        displayTrigger: true,
        displayTimer: 60_000,
        disabled: !shouldRestoreVolume || !isLargerThanWide,
      }}
    />
  );
};
